import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddProjectLeadRequest,
  ArchiveProjectRequest,
  AssignmentDto,
  AuditLogResponse,
  CreateAssignmentRequest,
  CreateLineClassificationRequest,
  UpdateLineClassificationRequest,
  CreateProjectRequest,
  CreateProjectTeamMembershipRequest,
  CreateTaskRequest,
  LineClassificationDto,
  LockProjectRequest,
  LockTaskRequest,
  PatchProjectRequest,
  PatchTaskRequest,
  ProjectArchiveImpact,
  ProjectDto,
  ProjectMemberDto,
  ProjectTeamMembershipDto,
  ProjectTeamSummaryDto,
  TaskDto,
  TaskTeamMembershipDto,
} from '@time-tracking/shared';

import { parseJson } from './client.js';
import { useApiFetch } from './useApiFetch.js';

// ---- Line classifications ------------------------------------------------

export function useClassifications() {
  const fetch = useApiFetch();
  return useQuery<LineClassificationDto[]>({
    queryKey: ['line-classifications'],
    queryFn: () =>
      fetch('/v1/line-classifications').then((r) => parseJson<LineClassificationDto[]>(r)),
  });
}

export function useCreateClassification() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<LineClassificationDto, Error, CreateLineClassificationRequest>({
    mutationFn: (data) =>
      fetch('/v1/line-classifications', {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => parseJson<LineClassificationDto>(r)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['line-classifications'] });
    },
  });
}

export function useUpdateClassification() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<
    LineClassificationDto,
    Error,
    { classificationId: string; patch: UpdateLineClassificationRequest }
  >({
    mutationFn: ({ classificationId, patch }) =>
      fetch(`/v1/line-classifications/${classificationId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }).then((r) => parseJson<LineClassificationDto>(r)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['line-classifications'] });
    },
  });
}

// ---- Projects ------------------------------------------------------------

/**
 * Every project the caller can see, archived ones included.
 *
 * This sends no query parameters and `GET /v1/projects` filters nothing, so this
 * one query is the sole data source for the whole project tree. Archived rows
 * are told apart by `archivedAt !== null` and partitioned in the tree, not
 * fetched separately — filtering them out server-side would leave the Archived
 * group empty and put Restore out of reach.
 */
export function useProjects() {
  const fetch = useApiFetch();
  return useQuery<ProjectDto[]>({
    queryKey: ['projects'],
    queryFn: () => fetch('/v1/projects').then((r) => parseJson<ProjectDto[]>(r)),
  });
}

// Full team roster for the project-setup "Add team…" palette picker.
export function useTeams() {
  const fetch = useApiFetch();
  return useQuery<ProjectTeamSummaryDto[]>({
    queryKey: ['teams'],
    queryFn: () => fetch('/v1/teams').then((r) => parseJson<ProjectTeamSummaryDto[]>(r)),
  });
}

export function useProject(projectId: string | null) {
  const fetch = useApiFetch();
  return useQuery<ProjectDto>({
    queryKey: ['projects', projectId],
    queryFn: () => fetch(`/v1/projects/${projectId}`).then((r) => parseJson<ProjectDto>(r)),
    enabled: Boolean(projectId),
  });
}

export function useCreateProject() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ProjectDto, Error, CreateProjectRequest>({
    mutationFn: (data) =>
      fetch('/v1/projects', { method: 'POST', body: JSON.stringify(data) }).then((r) =>
        parseJson<ProjectDto>(r),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function usePatchProject() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ProjectDto, Error, { projectId: string; patch: PatchProjectRequest }>({
    mutationFn: ({ projectId, patch }) =>
      fetch(`/v1/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }).then((r) => parseJson<ProjectDto>(r)),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

export function useLockProject() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ProjectDto, Error, { projectId: string; body: LockProjectRequest }>({
    mutationFn: ({ projectId, body }) =>
      fetch(`/v1/projects/${projectId}/lock`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => parseJson<ProjectDto>(r)),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

// ---- Archive / restore ----------------------------------------------------

/**
 * What archiving this project would cut: task and assignee counts, every minute
 * ever logged, submitted sheets still awaiting approval, and the teams that
 * staff it.
 *
 * Informational — the server never gates archive on it. The dialog gates its
 * confirm button on it anyway, because the "also free the project code" default
 * is derived from `loggedMinutes`: confirming before the number lands would send
 * a guess at the one irreversible-feeling half of the action.
 *
 * ONE CAVEAT for the restore direction, which reads the same endpoint:
 * `assignedUserCount` counts only assignments with `activeFlag = true`, and
 * archiving deactivated every one of them — so it reads 0 on an archived
 * project and must NOT be presented as "who comes back". `taskCount` and
 * `affectedTeams` are unaffected (archive touches neither tasks nor team
 * memberships) and stay truthful in both directions.
 */
export function useProjectArchiveImpact(projectId: string | null) {
  const fetch = useApiFetch();
  return useQuery<ProjectArchiveImpact>({
    queryKey: ['projects', projectId, 'archive-impact'],
    queryFn: () =>
      fetch(`/v1/projects/${projectId}/archive-impact`).then((r) =>
        parseJson<ProjectArchiveImpact>(r),
      ),
    enabled: Boolean(projectId),
  });
}

/**
 * Caches that stop being true the moment a project's staffing changes.
 *
 * Archive and restore both flip `assignments.active_flag` in bulk, and a lead is
 * very often a contributor too — the same session holds their own timesheet.
 * Invalidating only the project caches leaves that person looking at rows the
 * server would no longer seed, an outstanding-weeks badge computed from staffing
 * that no longer exists, and (after a code release) an approval view still
 * showing the old project code.
 *
 * Prefix keys, so `['timesheet', weekStartDate]` and friends are all covered.
 */
function invalidateStaffingDerived(queryClient: ReturnType<typeof useQueryClient>): void {
  for (const key of [['timesheet'], ['week-status'], ['outstanding-weeks'], ['approvals']]) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

export function useArchiveProject() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ProjectDto, Error, { projectId: string; body: ArchiveProjectRequest }>({
    mutationFn: ({ projectId, body }) =>
      fetch(`/v1/projects/${projectId}/archive`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => parseJson<ProjectDto>(r)),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      // Archiving the last project a person leads flips `leadsAnyProject`, which
      // is what puts Projects in the nav at all.
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      invalidateStaffingDerived(queryClient);
    },
  });
}

export function useRestoreProject() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ProjectDto, Error, { projectId: string }>({
    mutationFn: ({ projectId }) =>
      fetch(`/v1/projects/${projectId}/restore`, { method: 'POST' }).then((r) =>
        parseJson<ProjectDto>(r),
      ),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      // Mirror of archive: restoring can put Projects back in the nav.
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      invalidateStaffingDerived(queryClient);
    },
  });
}

// ---- Project leads (project-level) ---------------------------------------

export function useAddProjectLead(projectId: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ProjectDto, Error, AddProjectLeadRequest>({
    mutationFn: (data) =>
      fetch(`/v1/projects/${projectId}/leads`, {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => parseJson<ProjectDto>(r)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
      // Adding a lead can flip the current user's leadsAnyProject (Projects nav visibility).
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useRemoveProjectLead(projectId: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ProjectDto, Error, { userId: string }>({
    mutationFn: ({ userId }) =>
      fetch(`/v1/projects/${projectId}/leads/${userId}`, { method: 'DELETE' }).then((r) =>
        parseJson<ProjectDto>(r),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
      // Removing a lead can flip the current user's leadsAnyProject (Projects nav visibility).
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useProjectMembers(projectId: string | null) {
  const fetch = useApiFetch();
  return useQuery<ProjectMemberDto[]>({
    queryKey: ['projects', projectId, 'members'],
    queryFn: () =>
      fetch(`/v1/projects/${projectId}/members`).then((r) => parseJson<ProjectMemberDto[]>(r)),
    enabled: Boolean(projectId),
  });
}

// ---- Project team memberships (project-level) ----------------------------

export function useAddTeamToProject(projectId: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ProjectTeamMembershipDto, Error, CreateProjectTeamMembershipRequest>({
    mutationFn: (data) =>
      fetch(`/v1/projects/${projectId}/team-memberships`, {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => parseJson<ProjectTeamMembershipDto>(r)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
      // ProjectDto.teams (palette chips + sidebar team pills) lives on the
      // project detail/list payloads — refresh both so the change is visible.
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useRemoveTeamFromProject(projectId: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, { teamId: string }>({
    mutationFn: async ({ teamId }) => {
      const r = await fetch(`/v1/projects/${projectId}/team-memberships/${teamId}`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error(`API error: ${r.status}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
      // ProjectDto.teams (palette chips + sidebar team pills) lives on the
      // project detail/list payloads — refresh both so the change is visible.
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// ---- Activity (audit log) -------------------------------------------------

export function useProjectActivity(projectId: string | null) {
  const fetch = useApiFetch();
  return useQuery<AuditLogResponse>({
    queryKey: ['projects', projectId, 'activity'],
    queryFn: () =>
      fetch(`/v1/projects/${projectId}/activity`).then((r) => parseJson<AuditLogResponse>(r)),
    enabled: Boolean(projectId),
  });
}

// ---- Tasks ---------------------------------------------------------------

export function useTasks(projectId: string | null) {
  const fetch = useApiFetch();
  return useQuery<TaskDto[]>({
    queryKey: ['projects', projectId, 'tasks'],
    queryFn: () => fetch(`/v1/projects/${projectId}/tasks`).then((r) => parseJson<TaskDto[]>(r)),
    enabled: Boolean(projectId),
  });
}

export function useCreateTask() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<TaskDto, Error, { projectId: string; data: CreateTaskRequest }>({
    mutationFn: ({ projectId, data }) =>
      fetch(`/v1/projects/${projectId}/tasks`, {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => parseJson<TaskDto>(r)),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
    },
  });
}

export function usePatchTask() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<
    TaskDto,
    Error,
    { taskId: string; projectId: string; patch: PatchTaskRequest }
  >({
    mutationFn: ({ taskId, patch }) =>
      fetch(`/v1/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }).then((r) => parseJson<TaskDto>(r)),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
    },
  });
}

export function useLockTask() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<TaskDto, Error, { taskId: string; projectId: string; body: LockTaskRequest }>({
    mutationFn: ({ taskId, body }) =>
      fetch(`/v1/tasks/${taskId}/lock`, { method: 'POST', body: JSON.stringify(body) }).then((r) =>
        parseJson<TaskDto>(r),
      ),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
    },
  });
}

export interface DeleteTaskError extends Error {
  hours?: number;
}

export function useDeleteTask() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, DeleteTaskError, { taskId: string; projectId: string }>({
    mutationFn: async ({ taskId }) => {
      const r = await fetch(`/v1/tasks/${taskId}`, { method: 'DELETE' });
      if (r.status === 409) {
        const body = (await r.json().catch(() => ({}))) as { error?: string; hours?: number };
        const err: DeleteTaskError = new Error(body.error ?? 'Task has logged hours');
        if (body.hours !== undefined) err.hours = body.hours;
        throw err;
      }
      if (!r.ok) throw new Error(`API error: ${r.status}`);
    },
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
    },
  });
}

// ---- Task team memberships -----------------------------------------------

export function useTaskTeamMemberships(taskId: string | null) {
  const fetch = useApiFetch();
  return useQuery<TaskTeamMembershipDto[]>({
    queryKey: ['tasks', taskId, 'team-memberships'],
    queryFn: () =>
      fetch(`/v1/tasks/${taskId}/team-memberships`).then((r) =>
        parseJson<TaskTeamMembershipDto[]>(r),
      ),
    enabled: Boolean(taskId),
  });
}

export function useAddTeamToTask() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<
    TaskTeamMembershipDto,
    Error,
    { taskId: string; projectId: string; teamId: string }
  >({
    mutationFn: ({ taskId, teamId }) =>
      fetch(`/v1/tasks/${taskId}/team-memberships`, {
        method: 'POST',
        body: JSON.stringify({ teamId }),
      }).then((r) => parseJson<TaskTeamMembershipDto>(r)),
    onSuccess: (_data, { taskId, projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'team-memberships'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
    },
  });
}

export function useRemoveTeamFromTask() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, { taskId: string; projectId: string; teamId: string }>({
    mutationFn: async ({ taskId, teamId }) => {
      const r = await fetch(`/v1/tasks/${taskId}/team-memberships/${teamId}`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error(`API error: ${r.status}`);
    },
    onSuccess: (_data, { taskId, projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'team-memberships'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
    },
  });
}

// ---- Individual assignments ----------------------------------------------

export function useTaskAssignments(taskId: string | null) {
  const fetch = useApiFetch();
  return useQuery<AssignmentDto[]>({
    queryKey: ['tasks', taskId, 'assignments'],
    queryFn: () =>
      fetch(`/v1/tasks/${taskId}/assignments`).then((r) => parseJson<AssignmentDto[]>(r)),
    enabled: Boolean(taskId),
  });
}

export function useAddAssignment() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<
    AssignmentDto,
    Error,
    { taskId: string; projectId: string; data: CreateAssignmentRequest }
  >({
    mutationFn: ({ taskId, data }) =>
      fetch(`/v1/tasks/${taskId}/assignments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => parseJson<AssignmentDto>(r)),
    onSuccess: (_data, { taskId, projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
    },
  });
}

export function useRemoveAssignment() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, { taskId: string; assignmentId: string; projectId: string }>(
    {
      mutationFn: async ({ taskId, assignmentId }) => {
        const r = await fetch(`/v1/tasks/${taskId}/assignments/${assignmentId}`, {
          method: 'DELETE',
        });
        if (!r.ok) throw new Error(`API error: ${r.status}`);
      },
      onSuccess: (_data, { taskId, projectId }) => {
        void queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'assignments'] });
        void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
        void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
      },
    },
  );
}
